import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { LaboratoryResMock } from '../types/laboratory.type'

type Props = Partial<{
  page: number
  limit: number
  query: string
  sortDir: 'asc' | 'desc'
  id: string
  default: boolean
  sortBy: string
}>

export const useImagings = (arg?: Props) => {
  const page = arg?.page || 1
  const limit = arg?.limit || 10
  const query = arg?.query
  const sortDir = arg?.sortDir || 'asc'
  const id = arg?.id
  const defaultVal = arg?.default
  const sortBy = arg?.sortBy || 'created_at'

  const { data, isLoading: imagingIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock[]>>(`/imagings`, {
        params: {
          ...(page && { 'page[number]': page }),
          ...(limit && { 'page[size]': limit }),
          ...(query && { query }),
          ...(sortDir && { sort_dir: sortDir }),
          ...(sortBy && { sort_by: sortBy }),
          ...(defaultVal && { default: defaultVal })
        }
      })
      return data
    },
    queryKey: ['imagings', { defaultVal, limit, page, query, sortDir, sortBy }]
  })

  const { data: singleImaging } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock>>(`/imagings/${id}`)
      return data.data
    },
    queryKey: ['imagings', id]
  })

  const { mutateAsync: createImaging } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`imagings`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imagings'] })
    }
  })

  const { mutateAsync: updateImaging } = useMutation({
    mutationFn: async ({ data, id }: { data: any; id: string }) => {
      const { data: updatedData } = await AXIOS_INSTANCE.patch(`imagings/${id}`, data, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return updatedData
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imagings'] })
    }
  })

  const { mutateAsync: deleteImaging } = useMutation({
    mutationFn: async (id: string) => {
      await AXIOS_INSTANCE.delete(`imagings/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['imagings'] })
    }
  })

  return {
    ImagingPagination: data?.meta.pagination,
    createImaging,
    deleteImaging,
    imagingIsLoading,
    imagingList: data?.data,
    singleImaging,
    updateImaging
  }
}
